import React from 'react';
import { connect, ConnectedProps } from '@cerebral/react';
import { state, sequences } from 'app/pwa/app.cerebral';
import NotFound from 'dc/NotFound';

import {
  GlobalStyle,
  Wrapper,
  StatusBar,
  Main,
  InnerWrapper,
  TabMenu,
} from './elements';
import SideBar from './SideBar';
import Modals from './Modals';
import ChallengeErrorBoundary from 'challenge/common/ChallengeErrorBoundary';
import Tabs from './Tabs';
import { useConfig, useText, useLink } from 'common/hooks';
import InfoCard from 'dc/InfoCard';

const deps = {
  notFound: state.challenge.notFound,
  events: state.challenge.events.computedEvents,
  currentTab: state.challenge.currentTab,
  statusBar: state.challenge.info.statusBar,
  showTutorial: state.challenge.tutorial.show,
  tabClicked: sequences`challenge.tabMenuClicked`,
  setNumberInputUrl: sequences`challenge.info.setNumberInputUrl`,
};

export const AppDesktopComponent = connect(
  deps,
  function AppDesktop({
    notFound,
    events,
    currentTab,
    statusBar,
    showTutorial,
    tabClicked,
    setNumberInputUrl,
  }: typeof deps & ConnectedProps): React.ReactElement {
    const t = useText();
    const link = useLink();
    const { staticUrl } = useConfig();

    if (notFound) {
      return (
        <NotFound
          title={t('appContainer.not_found.title')}
          body={t('appContainer.not_found.body')}
          buttonText={t('appContainer.not_found.button_text')}
          imageSrc={`${staticUrl}/images/trophy_investigate.svg`}
          href={link('')}
        />
      );
    }

    return (
      <ChallengeErrorBoundary>
        <Wrapper $unclickable={showTutorial > 0}>
          <GlobalStyle />
          <StatusBar
            $show={statusBar.show}
            text={t(`challenge.status_bar.${statusBar.text}`)}
            buttonText={t(`challenge.status_bar.${statusBar.buttonText}`)}
            color={'#7A4E94'}
            icon={statusBar.icon}
            buttonOnClick={() => setNumberInputUrl()}
          />
          <Modals />
          <InnerWrapper>
            <SideBar />
            <Main>
              {currentTab === 0 ? (
                <InfoCard
                  title={t('challenge.info.calculator.better_than_average')}
                  text={t('challenge.info.calculator.footprint_calculator')}
                  canClose={false}
                />
              ) : null}
              <TabMenu
                alignment={'left'}
                changeTab={tab =>
                  tabClicked({
                    tab,
                    currentTab,
                  })
                }
                selected={currentTab}
                tabs={[
                  {
                    label: t('challenge.TabMen03.log'),
                    icon: 'icon-check_circle',
                  },
                  {
                    label: t('challenge.TabMen03.topList'),
                    icon: 'icon-leaderboard',
                  },
                  {
                    label: t('challenge.TabMen03.insights'),
                    icon: 'icon-sort',
                  },
                  {
                    label: t('challenge.TabMen03.latestEvents'),
                    icon: 'icon-notifications_none',
                    notifications: events?.newEvents?.length ?? 0,
                  },
                ]}
              />
              <ChallengeErrorBoundary>
                <Tabs />
              </ChallengeErrorBoundary>
            </Main>
          </InnerWrapper>
        </Wrapper>
      </ChallengeErrorBoundary>
    );
  }
);
